/*
 * facet-search.js — hydrates the SearchSpring-replacement faceted UI from the app index.
 *
 * Renders the theme's OWN classes dynamically (nothing hard-coded), matching production:
 *   - Top: <select class="xselect-single fullwidth select-ss_oem|ss_manufacturer|ss_model"> (select2)
 *   - <hr> + #category-facets accordion (.panel-group): current category expanded with
 *     sub-collection links; other categories link out.
 *   - Advanced Filters: .options-header + .checkbox lists with .facet-count (client refinement).
 *   - Find By Manufacturer: native manufacturer links.
 *   - Grid: .collection-item cards (p.variant-title) — variant level.
 *
 * Link facets (navigate, real URLs): category, sub-collections, manufacturers.
 * Client refinement (no reload): OEM + Model selects, Advanced Filter checkboxes, sort, page.
 * The SSR baseline (server-rendered grid + manufacturer list) is replaced on hydrate.
 */
(function () {
  'use strict';
  var CFG = window.FACET_CONFIG || {};
  var PER_PAGE = CFG.perPage || 24;
  var $ = window.jQuery;

  var DATA = [], CAT = null, CATS = [], BESTRANK = null;   // BESTRANK: product_id -> best-selling rank
  var state = { sel: {}, oem: '', model: '', manufacturer: '', sort: '', page: 1 };
  // Front-end sort options = Shopify's native collection sorts (value = Shopify sort_by key).
  // 'manual' uses the index's order, which is built in the collection's COLLECTION_DEFAULT order.
  var SORT_OPTS = [
    ['manual', 'Featured'],   // Shopify's "Manually" sort, shown to customers as "Featured"
    ['best-selling', 'Best selling'],
    ['title-ascending', 'Product title A-Z'],
    ['title-descending', 'Product title Z-A'],
    ['price-descending', 'Highest price'],
    ['price-ascending', 'Lowest price'],
    ['created-descending', 'Newest'],
    ['created-ascending', 'Oldest'],
  ];
  // search results page: rank by relevance (server pre-sorts), drop "Best selling" (no per-query rank)
  if (CFG.searchQuery) SORT_OPTS = [
    ['manual', 'Relevance'],
    ['title-ascending', 'Product title A-Z'], ['title-descending', 'Product title Z-A'],
    ['price-descending', 'Highest price'], ['price-ascending', 'Lowest price'],
    ['created-descending', 'Newest'], ['created-ascending', 'Oldest'],
  ];
  var selectsBuilt = false;

  function selArr(k) { return state.sel[k] || (state.sel[k] = []); }
  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  function money(d) { return (CFG.moneyFormat || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, Number(d || 0).toFixed(2)); }
  function handleize(s) { return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
  // strip the legacy SearchSpring "ss_" prefix from a filter field so URLs read cleanly
  // (filter.ss_manufacturer -> filter.manufacturer). loadURL strips it on read too, so any
  // older shared links with the ss_ prefix keep working.
  function pk(f) { return String(f == null ? '' : f).replace(/^ss_/, ''); }

  function specFacets() {
    return (CAT && CAT.advanced || []).map(function (a) { return { key: a.attr, label: a.label, get: function (r) { var v = r.specs[a.attr]; return v ? [v] : []; } }; });
  }

  // ---------- filtering ----------
  // Manufacturer is a client-side refinement (single-select), exactly like OEM/Model — NOT a redirect
  // to /collections/x/manufacturer-y. On a manufacturer landing page (CFG.activeManufacturerTag set)
  // it's seeded from that tag in boot(), so the same code path scopes the grid either way.
  function passManufacturer(r) { if (!state.manufacturer) return true; return (r.manufacturer || []).indexOf(state.manufacturer) >= 0; }
  function passModel(r) { if (!state.model) return true; return (r.model_paths || []).some(function (p) { return p === state.model || p.indexOf(state.model + ' > ') === 0; }); }
  function passOem(r) { if (!state.oem) return true; return (r.oems || []).indexOf(state.oem) >= 0; }
  function passSpec(r, except) {
    var f = specFacets();
    for (var i = 0; i < f.length; i++) { if (f[i].key === except) continue; var s = selArr(f[i].key); if (s.length && !f[i].get(r).some(function (v) { return s.indexOf(v) >= 0; })) return false; }
    return true;
  }
  // sub-collections are a client-side multi-select facet (key '__subcol'), combining with the rest
  function passSubcol(r, except) { if (except === '__subcol') return true; var s = selArr('__subcol'); return !s.length || s.indexOf(r.subcol) >= 0; }
  function refined(except) {
    return DATA.filter(function (r) {
      if (except !== 'manufacturer' && !passManufacturer(r)) return false;
      if (except !== 'oem' && !passOem(r)) return false;
      if (except !== 'model' && !passModel(r)) return false;
      if (!passSubcol(r, except)) return false;
      return passSpec(r, except);
    });
  }
  function counts(getVals, except) { var c = {}; refined(except).forEach(function (r) { getVals(r).forEach(function (v) { c[v] = (c[v] || 0) + 1; }); }); return c; }
  function sortEntries(c) {
    return Object.keys(c).map(function (k) { return [k, c[k]]; }).sort(function (a, b) {
      var na = parseFloat(a[0]), nb = parseFloat(b[0]);
      if (!isNaN(na) && !isNaN(nb) && /^\s*[\d.]/.test(a[0]) && /^\s*[\d.]/.test(b[0])) return na - nb;
      return a[0].toLowerCase().localeCompare(b[0].toLowerCase());
    });
  }

  // ---------- URL sync (shareable; no reload) ----------
  // build the querystring for the current state; pageOverride lets pagination links point at ?page=N
  function buildParams(pageOverride) {
    var p = new URLSearchParams();
    if (CFG.searchQuery) p.set('q', CFG.searchQuery);   // keep the search query in the URL (search mode)
    specFacets().forEach(function (f) { selArr(f.key).forEach(function (v) { p.append('filter.' + pk(CAT.advanced.filter(function (a) { return a.attr === f.key; })[0].field || f.key), v); }); });
    selArr('__subcol').forEach(function (v) { p.append('filter.' + pk((CAT && CAT.subcolField) || ('ss_' + CFG.collection)), v); });
    if (state.oem) p.set('filter.oem', state.oem);
    if (state.model) p.set('filter.manu_model_hierarchy', state.model);
    // skip when it equals the landing manufacturer tag (already in the path) to avoid a redundant param
    if (state.manufacturer && state.manufacturer !== CFG.activeManufacturerTag) p.set('filter.manufacturer', state.manufacturer);
    // mirror Shopify's native sort param so a shared/reloaded URL also drives the SSR grid
    if (state.sort && state.sort !== CFG.defaultSort) p.set('sort_by', state.sort);
    var pg = pageOverride != null ? pageOverride : state.page;
    if (pg > 1) p.set('page', pg);
    return p;
  }
  // keep <link rel="canonical"> self-referencing the current page. Page-only (mirrors the SSR canonical
  // policy that strips filters/sort): /collections/x -> /collections/x?page=2. location.pathname is
  // preserved so a manufacturer sub-path self-references too. Not touched on initial load (the SSR
  // canonical stays authoritative); updated here on every state change, incl. pagination clicks.
  function updateCanonical() {
    var link = document.querySelector('link[rel="canonical"]'); if (!link) return;
    link.setAttribute('href', location.origin + location.pathname + (state.page > 1 ? '?page=' + state.page : ''));
  }
  function syncURL() {
    var p = buildParams();
    try { history.replaceState(null, '', location.pathname + (p.toString() ? '?' + p.toString() : '')); } catch (e) {}
    updateCanonical();
  }
  // real, crawlable href for a page link (SEO): /collections/x?page=N (preserving filters/sort/q)
  function pageUrl(n) { var p = buildParams(n); return location.pathname + (p.toString() ? '?' + p.toString() : ''); }
  function loadURL() {
    var p = new URLSearchParams(location.search), byField = {};
    (CAT.advanced || []).forEach(function (a) { byField[pk(a.field)] = a.attr; byField[pk(a.attr)] = a.attr; });
    var subKey = pk((CAT && CAT.subcolField) || ('ss_' + CFG.collection));
    p.forEach(function (v, k) {
      if (k.indexOf('filter.') !== 0) return;
      var f = pk(k.slice(7));   // strip ss_ so we read both clean links and legacy ss_ links
      if (f === 'oem') state.oem = v;
      else if (f === 'manu_model_hierarchy') state.model = v;
      else if (f === 'manufacturer') state.manufacturer = v;
      else if (f === subKey) selArr('__subcol').push(v);
      else if (byField[f]) selArr(byField[f]).push(v);
    });
    // default to the collection's admin sort (collection.default_sort_by) so the initial order
    // matches the SSR grid; a ?sort_by= in the URL overrides it.
    state.sort = p.get('sort_by') || CFG.defaultSort || 'manual';
    state.page = parseInt(p.get('page'), 10) || 1;
  }

  // ---------- grid (native collection-item markup, like production) ----------
  // stock count shown next to the price, mirroring production (e.g. "0 in stock", "100+ in stock").
  // CFG.stockTemplate is the localized "{{count}} in stock" string with @@ standing in for the count.
  // stock message, mirroring the product page: a dropshipped product that's available shows
  // "Call for Stock"; dropshipped + unavailable shows "0 in Stock"; otherwise the normal
  // "N in stock" (100+ cap). Returns a <span class="stock"> or '' (no template/label).
  function stockHTML(r) {
    var msg;
    if (r.dropshipped === '1') {
      msg = (r.availability === '1') ? CFG.callForStockLabel : CFG.zeroStockLabel;
    } else if (CFG.stockTemplate) {
      var q = Number(r.inventory) || 0;
      var n = q > 100 ? '100+' : (q < 0 ? 0 : q);
      msg = CFG.stockTemplate.replace('@@', n);
    }
    return msg ? '<span class="stock">' + esc(msg) + '</span>' : '';
  }
  function cardHTML(r) {
    var url = '/products/' + encodeURIComponent(r.handle) + '?variant=' + encodeURIComponent(r.id);
    var price = '';
    if (CFG.showPrices) {
      var cents = Math.round((r.price || 0) * 100), cmp = Math.round((r.compare_at || 0) * 100);
      price = '<p class="variant-price sell-price">' +
        '<span class="price money" data-wpd-product-handle="' + esc(r.handle) + '" data-wpd-product-id="' + esc(r.product_id) +
        '" data-wpd-variant-id="' + esc(r.id) + '" data-wpd-variant-price="' + cents + '" data-wpd-variant-compare-at-price="' + cmp +
        '" data-wpd-product-collection-ids="' + esc((r.collection_ids || []).join(',')) + '">' + money(r.price) + '</span></p>';
    }
    // stock shown for everyone (ungated, like the product page), as its own line below the price
    var stockInner = stockHTML(r);
    var stock = stockInner ? '<p class="variant-stock">' + stockInner + '</p>' : '';
    var sku = r.sku ? '<p class="variant-sku">' + esc(r.sku) + '</p>' : '';
    // fall back to the theme's product placeholder when a variant has no image or the image 404s
    var ph = CFG.placeholderImage || '';
    var imgErr = ph ? ' onerror="this.onerror=null;this.src=\'' + ph + '\'"' : '';
    var altSrc = r.image_alt ? ' data-alt-src="' + esc(r.image_alt) + '"' : '';   // secondary image for hover swap
    return '<div class="collection-item"><a href="' + url + '">' +
      '<img class="variant-image" src="' + esc(r.image || ph) + '"' + altSrc + ' alt="' + esc(r.name || r.title) + '" loading="lazy"' + imgErr + '>' +
      '<p class="h4 variant-title">' + esc(r.title) + '</p>' +
      (r.variant_title ? '<p class="h5 variant-title">' + esc(r.variant_title) + '</p>' : '') + price + stock + sku + '</a></div>';
  }
  function sortRecs(recs) {
    // mirror Shopify's native collection sorts (client-side, over the variant records)
    switch (state.sort) {
      case 'title-ascending':  return recs.slice().sort(function (a, b) { return (a.title || '').localeCompare(b.title || ''); });
      case 'title-descending': return recs.slice().sort(function (a, b) { return (b.title || '').localeCompare(a.title || ''); });
      case 'price-ascending':  return recs.slice().sort(function (a, b) { return a.price - b.price; });
      case 'price-descending': return recs.slice().sort(function (a, b) { return b.price - a.price; });
      case 'created-ascending':  return recs.slice().sort(function (a, b) { return (a.created_at || 0) - (b.created_at || 0); });
      case 'created-descending': return recs.slice().sort(function (a, b) { return (b.created_at || 0) - (a.created_at || 0); });
      case 'best-selling':
        if (BESTRANK) {
          return recs.slice().sort(function (a, b) {
            var ra = BESTRANK[a.product_id] != null ? BESTRANK[a.product_id] : 1e9;
            var rb = BESTRANK[b.product_id] != null ? BESTRANK[b.product_id] : 1e9;
            return ra - rb;
          });
        }
        return recs;
      // 'manual' (and anything else) = the index's order, which is the collection's admin/default order
      default: return recs;
    }
  }
  // build the product_id -> best-selling rank map from the order array the proxy delivers
  function setBestSelling(order) {
    BESTRANK = null;
    if (order && order.length) { BESTRANK = {}; for (var i = 0; i < order.length; i++) if (BESTRANK[order[i]] == null) BESTRANK[order[i]] = i; }
  }
  // is the current view the default first page (no facets/sort/page) of a collection? If so we can
  // ADOPT the server-rendered grid instead of rebuilding it — keeps the painted SSR cards/images as
  // the LCP element (no swap), so SSR and hydrated initial views are literally the same DOM.
  function isDefaultState() {
    if (CFG.searchQuery) return false;          // search has no SSR grid to adopt
    if (state.page !== 1 || state.oem || state.model) return false;
    // a manufacturer selection that differs from the landing tag means the SSR grid isn't this view
    if (state.manufacturer && state.manufacturer !== (CFG.activeManufacturerTag || '')) return false;
    for (var k in state.sel) { if ((state.sel[k] || []).length) return false; }
    if (state.sort && state.sort !== (CFG.defaultSort || 'manual')) return false;
    return true;
  }
  function toolbarHTML() {
    var nSel = 0; Object.keys(state.sel).forEach(function (k) { nSel += (state.sel[k] || []).length; });
    if (state.model) nSel++; if (state.oem) nSel++; if (state.manufacturer) nSel++;
    // mobile-only "Filter Options" bar opens the sidebar drawer; toolbar: chips left, Sort right
    return '<button type="button" class="fs-filter-bar"><span class="fs-filter-ico" aria-hidden="true">&#9776;</span> ' +
      esc(CFG.filterLabel || 'Filter Options') + (nSel ? ' <span class="fs-filter-badge">' + nSel + '</span>' : '') + '</button>' +
      '<div class="collection-toolbar" style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:10px;margin-bottom:12px">' +
      '<div class="fs-applied-wrap" style="flex:1 1 auto;min-width:0">' + chipsHTML() + '</div>' +
      '<label class="text-muted" style="margin:0;white-space:nowrap">Sort by <select class="form-control fs-sort" style="display:inline-block;width:auto;min-width:170px;max-width:260px;height:auto;line-height:1.5;padding:6px 28px 6px 12px;vertical-align:middle">' +
      SORT_OPTS.map(function (o) { return '<option value="' + o[0] + '"' + (o[0] === state.sort ? ' selected' : '') + '>' + esc(o[1]) + '</option>'; }).join('') + '</select></label></div>';
  }
  function renderGrid(adopt) {
    var content = document.querySelector('.searchspring_content'); if (!content) return;
    var recs = refined(null), total = recs.length;
    var pages = Math.max(1, Math.ceil(total / PER_PAGE)); if (state.page > pages) state.page = pages;
    var existing = content.querySelector('.fs-card-grid');
    if (adopt && existing && existing.children.length) {
      // keep the SSR-painted cards (LCP image stays); only (re)build the toolbar + pagination around them
      Array.prototype.forEach.call(content.querySelectorAll('.fs-filter-bar, .collection-toolbar, .applied-filters, .fs-pagination'), function (el) { el.remove(); });
      existing.insertAdjacentHTML('beforebegin', toolbarHTML());
      existing.insertAdjacentHTML('afterend', paginationHTML(pages));
    } else {
      var slice = sortRecs(recs).slice((state.page - 1) * PER_PAGE, (state.page - 1) * PER_PAGE + PER_PAGE);
      content.innerHTML = toolbarHTML() + (slice.length ? '<div class="fs-card-grid">' + slice.map(cardHTML).join('') + '</div>'
        : '<p class="lead text-muted">No products match these filters.</p>') + paginationHTML(pages);
    }
    var sortSel = content.querySelector('.fs-sort'); if (sortSel) sortSel.onchange = function () { state.sort = this.value; state.page = 1; syncURL(); renderGrid(); };
    Array.prototype.forEach.call(content.querySelectorAll('.fs-page'), function (a) { a.onclick = function (e) { e.preventDefault(); state.page = +this.getAttribute('data-page'); syncURL(); renderGrid(); window.scrollTo(0, 0); }; });
    applyWholesale();
  }
  function chipsHTML() {
    var chips = [];
    selArr('__subcol').forEach(function (v) { chips.push('<button type="button" class="btn btn-xs btn-default fs-chip" data-k="__subcol" data-v="' + esc(v) + '">' + esc(v) + ' &times;</button>'); });
    specFacets().forEach(function (f) { selArr(f.key).forEach(function (v) { chips.push('<button type="button" class="btn btn-xs btn-default fs-chip" data-k="' + esc(f.key) + '" data-v="' + esc(v) + '">' + esc(f.label + ': ' + v) + ' &times;</button>'); }); });
    if (state.manufacturer) chips.push('<button type="button" class="btn btn-xs btn-default fs-chip" data-k="__manufacturer" data-v="' + esc(state.manufacturer) + '">Manufacturer: ' + esc(state.manufacturer) + ' &times;</button>');
    if (state.model) chips.push('<button type="button" class="btn btn-xs btn-default fs-chip" data-k="__model" data-v="' + esc(state.model) + '">Model: ' + esc(state.model) + ' &times;</button>');
    if (state.oem) chips.push('<button type="button" class="btn btn-xs btn-default fs-chip" data-k="__oem" data-v="' + esc(state.oem) + '">OEM: ' + esc(state.oem) + ' &times;</button>');
    if (!chips.length) return '';
    return '<div class="applied-filters" style="display:flex;flex-wrap:wrap;gap:6px;align-items:center;margin:0">' + chips.join('') + ' <button type="button" class="btn btn-xs btn-link fs-clear" style="margin:0">Clear all</button></div>';
  }
  function paginationHTML(pages) {
    if (pages <= 1) return '';
    var li = [], pg = state.page;
    // real ?page=N href (crawlable/SEO + no-JS fallback); JS intercepts the click for in-place render
    function a(n, lbl) { return '<li class="' + (n === pg ? 'active' : '') + '"><a href="' + esc(pageUrl(n)) + '" class="fs-page" data-page="' + n + '">' + (lbl || n) + '</a></li>'; }
    if (pg > 1) li.push(a(pg - 1, '&laquo;'));
    for (var n = 1; n <= pages; n++) { if (n === 1 || n === pages || Math.abs(n - pg) <= 2) li.push(a(n)); else if (n === 2 || n === pages - 1) li.push('<li class="disabled"><span>&hellip;</span></li>'); }
    if (pg < pages) li.push(a(pg + 1, '&raquo;'));
    return '<div class="row fs-pagination"><div class="col-xs-12 text-center"><ul class="pagination pagination-sm">' + li.join('') + '</ul></div></div>';
  }

  // ---------- sidebar: static shell (selects + containers + find-by-manufacturer) built ONCE ----------
  function selectHTML(cls, placeholder, entries, current) {
    return '<select class="xselect-single fullwidth ' + cls + '"><option value="">' + esc(placeholder) + '</option>' +
      entries.map(function (e) { return '<option value="' + esc(e[0]) + '"' + (current === e[0] ? ' selected' : '') + '>' + esc(e[0]) + '</option>'; }).join('') + '</select>';
  }
  function buildSidebarShell() {
    var sb = document.querySelector('.sidebar'); if (!sb) return;
    var oem = sortEntries(counts(function (r) { return r.oems || []; }, 'oem'));
    var manu = sortEntries(counts(function (r) { return r.manufacturer || []; }, 'manufacturer'));
    var modelC = counts(function (r) { return r.model_paths || []; }, 'model'), roots = {};
    Object.keys(modelC).forEach(function (p) { var t = p.split(' > ')[0]; roots[t] = (roots[t] || 0) + modelC[p]; });
    var modelEntries = Object.keys(roots).sort().map(function (t) { return [t, roots[t]]; });

    var html = '<button type="button" class="fs-drawer-close" aria-label="Close filters">&times;</button>' +
      '<div class="searchspring-facets"><div id="search-facets"><ul class="nav nav-pills nav-stacked">' +
      '<li>' + selectHTML('select-ss_oem', 'OEM Part # (Optional)', oem, state.oem) + '</li>' +
      '<li>' + selectHTML('select-ss_manufacturer', 'Manufacturer', manu, state.manufacturer) + '</li>' +
      '<li>' + selectHTML('select-ss_model', 'Model', modelEntries, state.model) + '</li>' +
      '</ul><hr><div id="fs-cat-facets"></div><div id="fs-adv-facets"></div></div></div>';

    // Find By Manufacturer links. On a collection -> the manufacturer sub-collection page. In SEARCH
    // mode there is no collection (CFG.collection is empty), so link to the search URL with the
    // manufacturer filter instead of "/collections//manufacturer-x" (which 404s).
    var manuLink = function (name) {
      return CFG.searchQuery
        ? '/search?q=' + encodeURIComponent(CFG.searchQuery) + '&filter.manufacturer=' + encodeURIComponent(name)
        : '/collections/' + encodeURIComponent(CFG.collection) + '/' + handleize('manufacturer ' + name);
    };
    html += '<div class="sidebar-manufacturers"><span class="panel-title h4">Find By Manufacturer</span><ul class="manufacturer-list list-unstyled" style="max-height:360px;overflow:auto">' +
      manu.map(function (e) { return '<li class="manufacturer-item list-item"><a href="' + esc(manuLink(e[0])) + '">' + esc(e[0]) + '</a></li>'; }).join('') + '</ul></div>';

    sb.innerHTML = html;

    // wire selects
    var oemSel = sb.querySelector('.select-ss_oem'), manuSel = sb.querySelector('.select-ss_manufacturer'), modelSel = sb.querySelector('.select-ss_model');
    oemSel.onchange = function () { state.oem = this.value; state.page = 1; syncURL(); renderFacets(); renderGrid(); };
    modelSel.onchange = function () { state.model = this.value; state.page = 1; syncURL(); renderFacets(); renderGrid(); };
    manuSel.onchange = function () { state.manufacturer = this.value; state.page = 1; syncURL(); renderFacets(); renderGrid(); };
    if ($ && $.fn && $.fn.select2) { try { $(sb).find('select.xselect-single').select2({ width: '100%' }); } catch (e) {} }
    selectsBuilt = true;
  }

  // ---------- top selects cross-filtering (co-occurrence) ----------
  // Re-render the OEM/Manufacturer/Model option lists so each shows only values that co-occur with
  // the OTHER active selections (counts' `except` already computes this): OEM<->Manufacturer<->Model
  // narrow each other in any order, and the collection/sub-collection scopes all three. The current
  // selection is always kept as an option (even if it falls outside the narrowed set) so it stays
  // visible/selected. Updates options in place (keeps the element + its onchange) and refreshes select2.
  function updateSelect(selEl, placeholder, entries, current) {
    if (!selEl) return;
    if (current && !entries.some(function (e) { return e[0] === current; })) entries = [[current, 0]].concat(entries);
    // signature is the OPTION LIST only (not the selection): a facet's own list never depends on its
    // own selection (the `except` rule), so the select being changed keeps the same sig and is skipped
    // -> we never destroy/re-init the select2 the user is actively interacting with; only others rebuild.
    var sig = entries.map(function (e) { return e[0]; }).join('|');
    if (selEl.getAttribute('data-fs-sig') === sig) return;   // option list unchanged -> skip rebuild
    selEl.setAttribute('data-fs-sig', sig);
    var hasS2 = $ && $.fn && $.fn.select2 && selEl.classList.contains('select2-hidden-accessible');
    if (hasS2) { try { $(selEl).select2('destroy'); } catch (e) {} }
    selEl.innerHTML = '<option value="">' + esc(placeholder) + '</option>' +
      entries.map(function (e) { return '<option value="' + esc(e[0]) + '"' + (current === e[0] ? ' selected' : '') + '>' + esc(e[0]) + '</option>'; }).join('');
    selEl.value = current || '';
    if (hasS2) { try { $(selEl).select2({ width: '100%' }); } catch (e) {} }
  }
  function refreshTopSelects() {
    var sb = document.querySelector('.sidebar'); if (!sb) return;
    var oem = sortEntries(counts(function (r) { return r.oems || []; }, 'oem'));
    var manu = sortEntries(counts(function (r) { return r.manufacturer || []; }, 'manufacturer'));
    var modelC = counts(function (r) { return r.model_paths || []; }, 'model'), roots = {};
    Object.keys(modelC).forEach(function (p) { var t = p.split(' > ')[0]; roots[t] = (roots[t] || 0) + modelC[p]; });
    var modelEntries = Object.keys(roots).sort().map(function (t) { return [t, roots[t]]; });
    updateSelect(sb.querySelector('.select-ss_oem'), 'OEM Part # (Optional)', oem, state.oem);
    updateSelect(sb.querySelector('.select-ss_manufacturer'), 'Manufacturer', manu, state.manufacturer);
    updateSelect(sb.querySelector('.select-ss_model'), 'Model', modelEntries, state.model);
  }

  // ---------- sidebar: dynamic facets (category accordion + advanced) re-rendered on change ----------
  function renderFacets() {
    var catBox = document.getElementById('fs-cat-facets'), advBox = document.getElementById('fs-adv-facets');
    if (!catBox || !advBox) return;

    // category accordion: current expanded with sub-collection CHECKBOXES (multi-select filter); others link out
    var subc = counts(function (r) { return r.subcol ? [r.subcol] : []; }, '__subcol');
    var cathtml = '<div class="options-header"><span class="sidebar-heading option-title h4">Category</span></div>' +
      '<div class="panel-group" id="accordionCategoryTypes">';
    CATS.forEach(function (c) {
      var on = CAT && c.handle === CAT.handle;
      cathtml += '<div class="panel panel-default"><div class="panel-heading"><h3 class="panel-title h4">' +
        '<a href="/collections/' + esc(c.handle) + '"' + (on ? '' : ' class="collapsed"') + '>' + esc(c.label) + '</a></h3></div>';
      if (on) {
        cathtml += '<div class="panel-collapse collapse in"><div class="panel-body">';
        (c.subcollections || []).forEach(function (s) {
          var title = (typeof s === 'string') ? s : s.title;
          var on = selArr('__subcol').indexOf(title) >= 0;
          if (subc[title] == null && !on) return;
          cathtml += '<div class="checkbox"><label><input type="checkbox" class="fs-check" data-k="__subcol" data-v="' + esc(title) + '"' + (on ? ' checked' : '') + '> ' + esc(title) + ' <span class="facet-count">' + (subc[title] || 0) + '</span></label></div>';
        });
        cathtml += '</div></div>';
      }
      cathtml += '</div>';
    });
    cathtml += '</div>';
    catBox.innerHTML = cathtml;

    // advanced filters: option-header + checkbox list with facet-count (client refinement)
    var advhtml = '';
    var facets = specFacets();
    if (facets.length) advhtml += '<h3 class="h4">' + esc(CAT.label) + ' Advanced Filters</h3>';
    facets.forEach(function (f) {
      var c = counts(f.get, f.key), s = selArr(f.key), entries = sortEntries(c);
      entries.sort(function (a, b) { return (s.indexOf(b[0]) >= 0 ? 1 : 0) - (s.indexOf(a[0]) >= 0 ? 1 : 0); });
      advhtml += '<div class="options-header"><span class="option-title h5">' + esc(f.label) + '</span></div><div class="options" style="max-height:280px;overflow:auto">' +
        (entries.map(function (e) {
          return '<div class="checkbox"><label><input type="checkbox" class="fs-check" data-k="' + esc(f.key) + '" data-v="' + esc(e[0]) + '"' + (s.indexOf(e[0]) >= 0 ? ' checked' : '') + '> ' +
            esc(e[0]) + ' <span class="facet-count">' + e[1] + '</span></label></div>';
        }).join('') || '<p class="text-muted small">None</p>') + '</div>';
    });
    advBox.innerHTML = advhtml;

    // wire ALL client checkboxes (sub-collections in the category accordion + advanced filters)
    Array.prototype.forEach.call(document.querySelectorAll('#fs-cat-facets .fs-check, #fs-adv-facets .fs-check'), function (cb) {
      cb.onchange = function () { var k = this.getAttribute('data-k'), v = this.getAttribute('data-v'), s = selArr(k), i = s.indexOf(v); if (i >= 0) s.splice(i, 1); else s.push(v); state.page = 1; syncURL(); renderFacets(); renderGrid(); };
    });
    // keep the OEM/Manufacturer/Model lists cross-filtered to the current selection set
    refreshTopSelects();
  }

  function wireChips() {
    var content = document.querySelector('.searchspring_content'); if (!content) return;
    content.addEventListener('click', function (e) {
      var el = e.target.closest ? e.target.closest('button') : null; if (!el) return;
      if (el.classList.contains('fs-chip')) {
        var k = el.getAttribute('data-k'), v = el.getAttribute('data-v');
        if (k === '__model') state.model = ''; else if (k === '__oem') state.oem = ''; else if (k === '__manufacturer') state.manufacturer = ''; else { var s = selArr(k), i = s.indexOf(v); if (i >= 0) s.splice(i, 1); }
        state.page = 1; syncURL(); buildSidebarShell(); renderFacets(); renderGrid();
      } else if (el.classList.contains('fs-clear')) { state.sel = {}; state.model = ''; state.oem = ''; state.manufacturer = ''; state.page = 1; syncURL(); buildSidebarShell(); renderFacets(); renderGrid(); }
    });
  }

  // Wholesale "Your Cost" pricing for the grid. The WPD app (app.bundle.js, loaded only for
  // logged-in tagged customers) rewrites the SSR .price[data-wpd-*] spans ONCE on page load and
  // never re-scans our dynamically re-rendered cards, so sorted/filtered/paginated cards would show
  // the native price. We replicate the app exactly: fetch the SAME wcp_discount_api endpoint for the
  // rendered handles (same-origin -> the customer's session cookie -> their tier price) and rewrite.
  // No-op for guests/retail (CFG.wholesale false) where the native price is already correct.
  var _wholesaleSeq = 0;
  // fetch one chunk of handles and rewrite matching price spans. The wcp_discount_api Liquid template
  // emits broken JSON if ANY handle in the batch is unknown, so we chunk (like the app, max 10) to
  // limit blast radius: a bad handle only forfeits its own chunk (those cards keep the native price).
  function wholesaleChunk(list, seq) {
    fetch('/search?view=wcp_discount_api&handle=' + list.map(encodeURIComponent).join(','),
          { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (seq !== _wholesaleSeq || !d || !Array.isArray(d.products)) return;   // superseded / bad payload
        var content = document.querySelector('.searchspring_content'); if (!content) return;
        var byVar = {};
        d.products.forEach(function (p) { (p.variants || []).forEach(function (v) { byVar[String(v.id)] = v; }); });
        Array.prototype.forEach.call(content.querySelectorAll('.price.money[data-wpd-variant-id]'), function (s) {
          var v = byVar[String(s.getAttribute('data-wpd-variant-id'))]; if (!v) return;
          var cents = v._wpd_price != null ? Number(v._wpd_price) : NaN;
          if (!isNaN(cents)) s.textContent = money(cents / 100);
        });
      })
      .catch(function (e) { if (window.console) console.warn('[facet-search] wholesale price refresh failed', e); });
  }
  function applyWholesale() {
    try { document.dispatchEvent(new CustomEvent('facet:rendered')); } catch (e) {}
    if (!CFG.wholesale || !CFG.showPrices) return;
    var content = document.querySelector('.searchspring_content'); if (!content) return;
    var spans = content.querySelectorAll('.price.money[data-wpd-variant-id]');
    if (!spans.length) return;
    var seen = {}, list = [];
    Array.prototype.forEach.call(spans, function (s) {
      var h = s.getAttribute('data-wpd-product-handle');
      if (h && !seen[h]) { seen[h] = 1; list.push(h); }
    });
    if (!list.length) return;
    var seq = ++_wholesaleSeq;   // only the latest render's responses may apply
    for (var i = 0; i < list.length; i += 10) wholesaleChunk(list.slice(i, i + 10), seq);
  }

  // ---------- mobile: facet sidebar as an off-canvas drawer (toggled by the Filter Options bar) ----------
  function openDrawer() {
    if (!document.querySelector('.fs-drawer-backdrop')) {
      var bd = document.createElement('div'); bd.className = 'fs-drawer-backdrop'; document.body.appendChild(bd);
    }
    document.body.classList.add('fs-drawer-open');
  }
  function closeDrawer() { document.body.classList.remove('fs-drawer-open'); }
  function wireDrawer() {
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest && t.closest('.fs-filter-bar')) { e.preventDefault(); openDrawer(); }
      else if ((t.classList && t.classList.contains('fs-drawer-backdrop')) || (t.closest && t.closest('.fs-drawer-close'))) { e.preventDefault(); closeDrawer(); }
    });
  }

  // hover image swap: on a card image with a secondary image (data-alt-src), swap src<->data-alt-src
  // on enter/leave (delegated, so it works for dynamically-rendered + SSR cards). Matches production.
  function wireImageSwap() {
    function swap(t) {
      if (!t || !t.classList || !t.classList.contains('variant-image')) return;
      if (!t.getAttribute('data-alt-src') || !(t.closest && t.closest('.fs-card-grid'))) return;
      var cur = t.getAttribute('src'); t.setAttribute('src', t.getAttribute('data-alt-src')); t.setAttribute('data-alt-src', cur);
    }
    document.addEventListener('mouseover', function (e) { swap(e.target); }, true);
    document.addEventListener('mouseout', function (e) { swap(e.target); }, true);
  }

  function boot() {
    if (!CFG.collection && !CFG.searchQuery) return;
    wireDrawer();
    wireImageSwap();
    // search results page (?q=) vs collection page (?collection=) — same engine, same rendering
    var endpoint = CFG.endpoint || '/apps/facets/index.json';
    var url = CFG.searchQuery
      ? endpoint + '?q=' + encodeURIComponent(CFG.searchQuery)
      : endpoint + '?collection=' + encodeURIComponent(CFG.collection);
    fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (!d || !d.cat || !Array.isArray(d.records)) return; // leave SSR baseline in place
        DATA = d.records; CAT = d.cat; CATS = d.cats || []; setBestSelling(d.bestSelling);
        loadURL();
        // landed on a sub-collection URL -> pre-select it as a checkbox facet on the parent grid
        if (d.activeSubcollection) { var s = selArr('__subcol'); if (s.indexOf(d.activeSubcollection) < 0) s.push(d.activeSubcollection); }
        // landed on a manufacturer URL (/collections/x/manufacturer-y) -> seed the manufacturer filter
        // so the same client-side refinement path scopes the grid (and pre-selects the dropdown)
        if (CFG.activeManufacturerTag && !state.manufacturer) state.manufacturer = CFG.activeManufacturerTag;
        // Grid first (adopt SSR cards on the default view -> the SSR product image is the LCP, no swap).
        // Then build the heavy sidebar (select2 over large OEM/Model lists) AFTER a paint, so that
        // long synchronous task doesn't block the LCP image from painting.
        renderGrid(isDefaultState()); wireChips();
        var buildSidebar = function () { buildSidebarShell(); renderFacets(); };
        if (window.requestAnimationFrame) requestAnimationFrame(function () { requestAnimationFrame(buildSidebar); });
        else setTimeout(buildSidebar, 0);
      })
      .catch(function (e) { if (window.console) console.error('[facet-search]', e); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
